import { updateDoc } from 'firebase/firestore';
import { uploadBytes } from 'firebase/storage';
import { when } from 'mobx';

import { functionAuthRequest } from 'services/API';
import { getAppStorageFileRef, getHostingBucket } from 'services/Firebase';
import { DocumentStatus } from 'types/DocumentStatus';
import {
    isTerminalProcessingStatus,
    ProcessingStatus,
} from 'types/ProcessingStatus';
import { delay } from 'utils/Async';
import { slashJoin } from 'utils/String';

import type Creative from './Creative';
import DocumentCollection from './DocumentCollection';
import Template, { TemplateSchema } from './Template';
import TemplateUploadManager from './TemplateUploadManager';

async function checkTemplateReady(template: Template) {
    // FIXME waits forever if processing ends in any terminal status other
    //  than COMPLETE, so upload() never settles. Needs a failure path -
    //  `when`'s timeout option, or match isTerminalProcessingStatus and
    //  reject on the non-COMPLETE ones.
    await when(() => template.data.status === ProcessingStatus.COMPLETE);
    // ??? reason for this delay is unrecorded - possibly settling time
    //  for the backend before the updateDoc/load that follow in upload
    await delay(100);
    return true;
}

export default class Templates extends DocumentCollection<
    Template,
    TemplateSchema
> {
    // The base declares `parent: DocumentStore<any>`, but our constructor only
    // ever accepts the Creative that owns us. Narrowing it here is what lets
    // the methods below reach that creative instead of asking RootStore which
    // one is currently on screen. `declare` is type-only and emits nothing, so
    // becomeObservable() sees exactly the own properties it saw before.
    declare parent: Creative;

    baseURL = getHostingBucket().websiteOrigin;
    uploadManager = new TemplateUploadManager(this);

    filterReady() {
        return this.all.filter(
            (item) => item.data.status === ProcessingStatus.COMPLETE
        );
    }

    filterProcessing() {
        return this.children.filter(
            (item) => !isTerminalProcessingStatus(item.data.status)
        );
    }

    constructor(parent: Creative) {
        super(parent, Template);
        this.becomeObservable();
    }

    upload = async (
        templateName: string,
        selectedFile: File,
        isUpdate: boolean,
        replaceAssets: boolean
    ) => {
        const firstTemplate = this.all.length === 0;
        let template = this.getByName(templateName);

        const nowMillis = Date.now();
        const randomNumber = Math.floor(Math.random() * 10000000);
        const path = `templates/${this.parent.id}/${nowMillis}_${randomNumber}/${selectedFile.name}`;
        const fileRef = getAppStorageFileRef(path);
        try {
            await uploadBytes(fileRef, selectedFile); //create a child directory called images, and place the file inside this directory
        } catch (e) {
            // Handle unsuccessful uploads
            console.log('Template upload failed:', e);
            throw new Error('Template upload failed.');
        }

        if (!template) {
            const templateProperties = new TemplateSchema();
            templateProperties.name = templateName;
            templateProperties.status = ProcessingStatus.COMPLETE; // This must be complete so that we can create a document reference before calling replaceCreativeTemplate.
            templateProperties.revision = 0;

            template = await this.add(templateProperties);
            template.data.id = template.docRef.id;
            template.data.status = ProcessingStatus.PENDING;
            replaceAssets = true;
        }

        try {
            const response = await functionAuthRequest(
                'replaceCreativeTemplate',
                {
                    id: template.id,
                    name: templateName,
                    storage: path,
                    replaceExtractedAssets: replaceAssets,
                },
                this
            );
        } catch (error) {
            // Handle unsuccessful uploads
            console.log(
                'Templates: Update template failed:',
                JSON.stringify(error)
            );
            throw new Error('Templates: Update template failed.');
            // this.remove(templateName);
        }

        await checkTemplateReady(template);
        await updateDoc(template.docRef, { storage: fileRef.fullPath });
        await template.load();

        const creative = this.parent;

        // Missing affordance: nothing warns when the uploaded template
        // declares a property that the creative already has under the same
        // name but a different type. That used to be checked here - the
        // template's properties were compared against the creative's, and the
        // conflicts stored for the old TemplatesDrawer to show in an
        // UploadConflictModal. The drawer went in e49419de (2022-09), and the
        // check carried on with nobody reading its result until it was removed
        // too. Restoring it would mean a new UI and testing, i.e. a feature.
        //
        // Nothing replaced it. The backend's template processing never looks
        // at the creative's properties, and merge() - which both
        // setDefaultDynamicProperties below and newFromTemplate use - sets a
        // same-named property's value whatever its type.

        if (firstTemplate) {
            console.log(
                `Templates: ${templateName}: First template finished processing. Setting default dynamic properties.`
            );
            creative.setDefaultDynamicProperties(template);
            await creative.saveDynamicProperties();
            await creative.save();
        }

        if (!isUpdate) {
            creative.ads.newFromTemplate(template); // <-- START HERE
        }

        return templateName;
    };

    remove = async (id: string) => {
        this.status = DocumentStatus.LOADING;

        const template = this.getById(id);
        const response = await functionAuthRequest(
            'deleteCreativeTemplateById',
            {
                id,
            },
            this
        );

        if (template) {
            template.data.status = ProcessingStatus.PENDING;
        }

        if (response.result === 'success') {
            console.log(
                'Templates: Successfully removed template',
                template?.data.name
            );
        } else {
            template?.delete();
        }

        this.status = DocumentStatus.NONE;
        return id;
    };

    getHostedURL = (id: string) => {
        const template = this.getByName(id);
        if (template) {
            const entryPath = slashJoin(
                template.data.prefix,
                template.data.entry
            );
            const baseURL = this.baseURL;
            if (!baseURL) return entryPath;
            const url = new URL(entryPath, baseURL);
            return String(url);
        }
        return '';
    };

    reset = () => {
        super.reset();
        this.baseURL = getHostingBucket().websiteOrigin;
        this.uploadManager.reset();
    };
}
